import { CustomerZoneGetResponseType } from "@sellar-io/api-v2/types";
import { InputText } from "@sellar-io/components";
import { useUniqueDirectPostcodes } from "../useUniqueDirectPostcodes";
import { InputLabel, InputContainer } from "@sellar-io/components";
import {
  Anchor,
  Button,
  Card,
  CardContent,
  CardHeader,
  Icon,
  InputTextArea,
} from "@sellar-io/components";
import { useRemoteFeatureFlags } from "../../../hooks/useRemoteFeatureFlags";
import { useSellarCurrentBusiness } from "../../../hooks/useSellarCurrentBusiness.shared";
import { useCustomerZonesUpdate } from "../../../hooks/api/v2/customerZones/useCustomerZonesUpdate";
import { useSupplier } from "../../../hooks/api/v2/supplier/useSupplier";
import { useState } from "react";
import { useSellarDistributionAvailabilityDeliveryPostcodeList } from "~/hooks/api/v2/sellarDistributionAvailability/useSellarDistributionAvailabilityDeliveryPostcodeList";

export const CustomerZoneDetails = ({
  customerZone,
}: {
  customerZone?: CustomerZoneGetResponseType;
}) => {
  const { business } = useSellarCurrentBusiness();
  const supplierId = business?.Supplier?.id;
  const sellarDistributionSetupFlag = useRemoteFeatureFlags().isActive(
    "sellarDistributionSetup",
  );

  const sellarDistributionPostcodeList =
    useSellarDistributionAvailabilityDeliveryPostcodeList(
      {
        query: [
          {
            type: "SupplierId",
            value: supplierId ?? 0,
            modifier: "EQUALS",
          },
        ],
      },
      {
        enabled: sellarDistributionSetupFlag && !!supplierId,
      },
    );

  const { data: supplier } = useSupplier(supplierId ?? 0, {
    enabled: !!supplierId,
  });

  const savedDeliveryNotes = customerZone?.systemDefinedType
    ? null
    : (customerZone?.deliveryNotes ??
      (customerZone?.type === "direct"
        ? (supplier?.directDeliveryNotes ?? "")
        : (supplier?.deliveryNotes ?? "")));
  const [deliveryNotes, setDeliveryNotes] = useState(savedDeliveryNotes ?? "");
  const [previousSavedDeliveryNotes, setPreviousSavedDeliveryNotes] =
    useState(savedDeliveryNotes);
  if (savedDeliveryNotes !== previousSavedDeliveryNotes) {
    setPreviousSavedDeliveryNotes(savedDeliveryNotes);
    if (savedDeliveryNotes !== null) {
      setDeliveryNotes(savedDeliveryNotes);
    }
  }

  const customerZoneUpdateMutation = useCustomerZonesUpdate(
    customerZone?.id ?? 0,
  );

  const postcodes =
    sellarDistributionPostcodeList.data?.pages.flatMap((page) =>
      page.map((postcode) => postcode.postcodeComponent),
    ) ?? [];

  const uniqueDirectPostcodes = useUniqueDirectPostcodes(
    sellarDistributionSetupFlag &&
      customerZone?.systemDefinedType === "sellarDistribution"
      ? postcodes
      : undefined,
  );

  const updateDeliveryNotes = () => {
    if (!customerZone) return;
    customerZoneUpdateMutation.mutate({ deliveryNotes });
  };

  return (
    <div className="flex flex-col gap-4">
      <Card className="bg-white">
        <CardHeader className="flex items-center bg-white p-4 font-semibold">
          Details
        </CardHeader>
        <CardContent className="flex flex-col gap-1 p-4">
          <InputLabel>Name</InputLabel>
          <InputContainer className="relative flex w-full items-center gap-1">
            <InputText value={customerZone?.name ?? ""} disabled={true} />
          </InputContainer>
        </CardContent>
      </Card>
      {!customerZone?.systemDefinedType && (
        <Card className="bg-white">
          <CardHeader className="flex flex-col items-start gap-1 bg-white p-4">
            <span className="font-semibold">Delivery notes</span>
            <p>Shown to all {customerZone?.name} customers; new and existing</p>
          </CardHeader>
          <CardContent className="flex flex-col gap-1 p-4">
            <div className="flex flex-col items-end gap-2">
              <InputContainer className="w-full">
                <InputTextArea
                  variant="legacyFixed"
                  value={deliveryNotes}
                  onChange={(e) => setDeliveryNotes(e.target.value)}
                  className="h-22.5"
                />
              </InputContainer>
              <Button
                type="submit"
                loading={customerZoneUpdateMutation.isPending}
                onClick={updateDeliveryNotes}
                disabled={deliveryNotes === savedDeliveryNotes}
              >
                Save changes
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
      <Card className="bg-white">
        <CardHeader className="flex flex-col items-start gap-1 bg-white p-4">
          <span className="font-semibold">Postcodes</span>
          <p>
            Any customers whose postcode is described below, will fall into this
            customer zone
            <br />
            <br />
            {customerZone?.systemDefinedType === "sellarDistribution" ? (
              "These postcodes are system defined and cannot be edited"
            ) : (
              <Anchor
                className="inline-flex items-center gap-1 text-blue-600 underline"
                href="https://intercom.help/sellario/en/articles/9981916-supplier-guide-delivery-settings#h_d37178a34b"
                target="_blank"
                rel="noopener noreferrer"
              >
                Where have these postcodes come from?
                <Icon icon="ARROW_TOP_RIGHT" size={0.6} />
              </Anchor>
            )}
          </p>
        </CardHeader>
        <CardContent className="flex flex-col gap-1 p-4">
          {customerZone?.default
            ? "All postcodes"
            : uniqueDirectPostcodes.tagList}
        </CardContent>
      </Card>
    </div>
  );
};
